// views/ForgotPassword/ForgotPassword.jsx
import './ForgotPassword.css';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { Lock } from 'lucide-react';
import PasswordRequirements from '../../components/PasswordRequirements/PasswordRequirements.jsx';

const SEND_FORGOT_PASSWORD_EMAIL = gql`
  mutation SendForgotPasswordEmail($email: String!) {
    sendForgotPasswordEmail(email: $email) {
      success
      message
    }
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($email: String!, $code: String!, $newPassword: String!) {
    resetPassword(email: $email, code: $code, newPassword: $newPassword) {
      success
      message
    }
  }
`;

export default function ForgotPassword() {
  const [email, setEmail]         = useState('');
  const [view,  setView]          = useState('forgot'); // forgot | reset | success
  const [err,   setErr]           = useState('');
  const [reset, setReset]         = useState({ code:'', newPassword:'', confirmPassword:'' });

  /* ---------------- GraphQL ---------------- */
  const [sendMail, { loading:sending }] = useMutation(SEND_FORGOT_PASSWORD_EMAIL,{
    variables:{ email },
    onCompleted:d=>d.sendForgotPasswordEmail.success
                   ? (setView('reset'), setErr(''))
                   : setErr(d.sendForgotPasswordEmail.message || 'Something went wrong'),
    onError:e=>setErr(e.message)
  });

  const [resetPass, { loading:resetting }] = useMutation(RESET_PASSWORD,{
    onCompleted:d=>d.resetPassword.success
                   ? (setView('success'), setErr(''))
                   : setErr(d.resetPassword.message || 'Failed to reset password'),
    onError:e=>setErr(e.message)
  });

  /* ---------------- Handlers --------------- */
  const onEmailSubmit = e =>{
    e.preventDefault();
    if(!email) return setErr('Please enter your email');
    sendMail();
  };

  const onResetSubmit = e =>{
    e.preventDefault();
    const { code,newPassword,confirmPassword } = reset;
    if(!code)               return setErr('Enter the verification code');
    if(!newPassword)        return setErr('Enter a new password');
    if(newPassword!==confirmPassword) return setErr('Passwords do not match');
    if(newPassword.length<8) return setErr('Password must be at least 8 characters');
    resetPass({ variables:{ email,code,newPassword }});
  };

  const onResetChange = e => setReset({ ...reset, [e.target.name]:e.target.value });

  /* ------------------- UI ------------------ */
  return (
    <>
      <div className="forgot-password-container">

        <div className="auth-card">
          {/* ---------- FORGOT ---------- */}
          {view==='forgot' && (
            <form onSubmit={onEmailSubmit} className="card-form">
              <div className="icon-circle"><Lock size={32} strokeWidth={1.8}/></div>
              <h2 className="card-title">Trouble logging in?</h2>
              <p className="card-text">
                Enter your email and we’ll send you a code to get back into your account.
              </p>

            
              <input
                className="auth-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={e=>setEmail(e.target.value)}
                disabled={sending}
              />

              {err && <p className="error-message">{err}</p>}

              <button className="primary-button" type="submit" disabled={sending||!email}>
                {sending ? 'Sending…' : 'Send verification code'}
              </button>

              <div className="divider"><span>OR</span></div>

              <button
                type="button"
                className="link-button bold"
                onClick={()=>window.location.href='/signup'}
              >
                Create new account
              </button>
            </form>
          )}

          {/* ---------- RESET ---------- */}
          {view==='reset' && (
            <form onSubmit={onResetSubmit} className="card-form">
              <h2 className="card-title">Reset password</h2>
              <p className="card-text">
                Enter the verification code sent to your email and choose a new password.
              </p>

              <label className="field-label">Verification code</label>
              <input
                className="auth-input"
                type="text"
                name="code"
                value={reset.code}
                onChange={onResetChange}
                placeholder="Code"
                disabled={resetting}
              />

              <label className="field-label">New password</label>
              <input
                className="auth-input"
                type="password"
                name="newPassword"
                value={reset.newPassword}
                onChange={onResetChange}
                placeholder="New password"
                disabled={resetting}
              />
              <PasswordRequirements password={reset.newPassword}/>

              <label className="field-label">Confirm password</label>
              <input
                className="auth-input"
                type="password"
                name="confirmPassword"
                value={reset.confirmPassword}
                onChange={onResetChange}
                placeholder="Confirm password"
                disabled={resetting}
              />

              {err && <p className="error-message">{err}</p>}

              <button className="primary-button" type="submit" disabled={resetting}>
                {resetting ? 'Resetting…' : 'Reset password'}
              </button>

              <button
                type="button"
                className="link-button"
                onClick={()=>setView('forgot')}
              >
                ← Back
              </button>
            </form>
          )}

          {/* ---------- SUCCESS ---------- */}
          {view==='success' && (
            <div className="success-message">
              <h2>Password reset successful!</h2>
              <p>You can now log in with your new password.</p>
              <button
                className="primary-button"
                onClick={()=>window.location.href='/login'}
              >
                Go to login
              </button>
            </div>
          )}

          {/* ---------- Barra Back to login ---------- */}
          <div className="bottom-card-bar" onClick={()=>window.location.href='/login'}>
            Back to login
          </div>
        </div>

      </div>
    </>
  );
}
